/**
 * @file ResourcePropertiesModal.js
 * @description Tabbed rich-text editor modal for Resource overview and transcript fields.
 */

import { useRef, useState } from 'react';
import classNames from 'classnames';
import RichTextEditor from '../../new-portal-page/RichTextEditor';
import { getFileCategory } from '../../helpers/FileUtils';
import { getText } from '../../../i18n';
import styles from '../AssetDrawer.module.scss';

const ResourcePropertiesModal = ({
    asset,
    initialTab = 'overview',
    onClose,
    onSave,
}) => {
    const [activeTab, setActiveTab] = useState(initialTab);

    const [overviewValue, setOverviewValue] = useState(asset.overview || '');
    const [transcriptValue, setTranscriptValue] = useState(
        asset.transcript || '',
    );

    const overviewInitialContent = useRef(asset.overview || '').current;
    const transcriptInitialContent = useRef(asset.transcript || '').current;

    const fileCategory = getFileCategory(asset.fileType);
    const canEditTranscript =
        fileCategory === 'video' || fileCategory === 'audio';

    const saveProperties = () => {
        onSave({
            overview: overviewValue,
            transcript: transcriptValue,
        });
        onClose();
    };

    return (
        <div className={styles.resourceModalOverlay}>
            <div
                className={styles.resourceModal}
                role="dialog"
                aria-modal="true"
            >
                <div className={styles.resourceModalHeader}>
                    <p>{getText('msg_0894683fbd64')}</p>
                    <button type="button" onClick={onClose}>
                        <i className="bi bi-x" /> {getText('msg_bbfa773e5a63')}
                    </button>
                </div>

                <div className={styles.resourceTabs} role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'overview'}
                        className={`${styles.resourceTab} ${activeTab === 'overview' ? styles.resourceTabActive : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        {getText('msg_0efc2e6be4c2')}
                    </button>
                    {canEditTranscript && (
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'transcript'}
                            className={`${styles.resourceTab} ${activeTab === 'transcript' ? styles.resourceTabActive : ''}`}
                            onClick={() => setActiveTab('transcript')}
                        >
                            {getText('msg_5bcd602254f3')}
                        </button>
                    )}
                </div>

                <div
                    className={classNames({
                        [styles.tabPanelHidden]: activeTab !== 'overview',
                    })}
                >
                    <p className={styles.resourceSectionTitle}>
                        {getText('msg_0efc2e6be4c2')}
                    </p>
                    <p className={styles.resourceHelperText}>
                        {getText('msg_878747748db7')}
                    </p>
                    <div className={styles.resourceEditorWrapper}>
                        <RichTextEditor
                            initialContent={overviewInitialContent}
                            onContentUpdate={setOverviewValue}
                            disableSSO={true}
                            disablePortalLink={true}
                            disableAssetLink={true}
                            disableHomeLink={true}
                        />
                    </div>
                </div>

                {canEditTranscript && (
                    <div
                        className={classNames({
                            [styles.tabPanelHidden]: activeTab !== 'transcript',
                        })}
                    >
                        <p className={styles.resourceSectionTitle}>
                            {getText('msg_5bcd602254f3')}
                        </p>
                        <p className={styles.resourceHelperText}>
                            {getText('msg_fb24565f5b76')}
                        </p>
                        <div className={styles.resourceEditorWrapper}>
                            <RichTextEditor
                                initialContent={transcriptInitialContent}
                                onContentUpdate={setTranscriptValue}
                                disableSSO={true}
                                disablePortalLink={true}
                                disableAssetLink={true}
                                disableHomeLink={true}
                            />
                        </div>
                    </div>
                )}

                <div className={styles.resourceModalActions}>
                    <button
                        type="button"
                        className="button-base ing-button-primary-left"
                        onClick={saveProperties}
                    >
                        {getText('msg_efc007a393f6')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResourcePropertiesModal;
